import React, { useEffect } from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import Stars from '../../../components/Badge/Stars';
import DoughnutChart from '../../../components/Charts/DoughnutChart';
import TrendDownIcon from '../../../components/Icons/TrendDownIcon';
import TrendUpIcon from '../../../components/Icons/TrendUpIcon';
import Tooltip from '../../../components/Tooltip/Tooltip';
import useDistributionInsight from '../../../hooks/dashboard/driver-list/useDistributionInsight';

const IncidentDistribution = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const { getDistributionDataInsight, distributionInsightData } =
    useDistributionInsight();

  useEffect(() => {
    if (driverId) {
      getDistributionDataInsight(driverId);
    }

    // eslint-disable-next-line
  }, [driverId]);

  const distribution = distributionInsightData?.incidentDistribution;

  const incidentDistributionData = [
    {
      name: 'Theft Reports',
      value: distribution?.theftReports ?? 0,
      fill: '#5B91F5',
    },
    {
      name: 'Accident Reports',
      value: distribution?.accidentReports ?? 0,
      fill: '#B7CAF0',
    },
  ];

  const totalReports =
    distribution?.totalReports ??
    incidentDistributionData.reduce((sum, item) => sum + item.value, 0);

  const avgTheft = distribution?.avgTheftSeverity ?? 0;
  const avgAccident = distribution?.avgAccidentSeverity ?? 0;
  const reportRate =
    typeof distribution?.reportRate === 'number' ? distribution!.reportRate : 0;
  const rateClass = reportRate >= 0 ? 'green' : reportRate < 0 ? 'red' : '';
  const rateLabel = `${reportRate >= 0 ? '+' : ''}${reportRate}%`;
  const rateDescription =
    reportRate > 0
      ? 'Report rate increase from last week'
      : reportRate < 0
        ? 'Report rate decrease from last week'
        : 'Report rate unchanged from last week';

  return (
    <Container>
      <HeaderArea>
        <Header>
          Incident Distribution{' '}
          <Tooltip content='Real-time incident analytics and insights' />
        </Header>

        {/* <ControlArea>
          <IncidentDataFilter setDataUrl={setDataUrl} dataUrl={dataUrl} />
        </ControlArea> */}
      </HeaderArea>

      {/*  */}
      <ContentArea>
        {/* Plot Area */}
        <DoughnutBox>
          <DoughnutBoxSize>
            <DoughnutChart
              data={incidentDistributionData}
              showLegend={false}
              tooltipLabel='Incident Count'
              centerLabel='Total Reports'
              centerValue={totalReports}
              aspect={1}
              innerRadius='86%'
              outerRadius='98%'
            />
          </DoughnutBoxSize>
          <DoughnutBoxRightContent>
            <LegendList>
              {incidentDistributionData.map((item) => (
                <LegendItem key={item.name}>
                  <LegendItemLeft>
                    <LegendSwatch color={item.fill} />
                    <LegendName>{item.name}</LegendName>
                  </LegendItemLeft>
                  <LegendValue>{item.value}</LegendValue>
                </LegendItem>
              ))}
            </LegendList>
            <DoughnutHint>
              Distribution data is derived from cumulative insights based on
              historical company data.
            </DoughnutHint>
          </DoughnutBoxRightContent>
        </DoughnutBox>
        <LeftBox>
          <StarsBox>
            <StarsContentRow>
              <StarRectangle>
                <StarHeader>Avg. Theft Report Severity</StarHeader>
                <StarBox>
                  <Stars numStars={avgTheft} /> {avgTheft}/5
                </StarBox>
              </StarRectangle>

              <StarRectangle>
                <StarHeader>Avg. Accident Report Severity</StarHeader>
                <StarBox>
                  <Stars numStars={avgAccident} /> {avgAccident}/5
                </StarBox>
              </StarRectangle>
            </StarsContentRow>
            <FooterStarContent>
              <StatsArea className={rateClass}>
                {rateLabel}{' '}
                {Number(reportRate) >= 0 ? <TrendUpIcon /> : <TrendDownIcon />}
              </StatsArea>
              <div>{rateDescription}</div>
            </FooterStarContent>
          </StarsBox>
        </LeftBox>
      </ContentArea>
    </Container>
  );
};

export default IncidentDistribution;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 24px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const HeaderArea = styled.div`
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  /* border: 1px solid red; */
  margin-bottom: 18px;
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 18px;
`;

// const ControlArea = styled.div`
//   display: flex;
// `;

const ContentArea = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
  }
`;

const DoughnutBox = styled.div`
  display: grid;
  grid-template-columns: 169px 1fr;
  gap: 28px;
  padding-right: 14px;

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
  }
`;

const DoughnutHint = styled.div`
  color: #41415a;
  font-size: 10px;
  font-weight: 400;
  margin-top: 20px;
`;

const StarsBox = styled.div`
  background-color: white;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const StarsContentRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 18px;
  background-color: #fff;

  @media (max-width: 884px) {
    display: flex;
    flex-direction: column;
  }
`;

const FooterStarContent = styled.div`
  display: flex;
  align-items: center;
  color: #71748c;
  gap: 8px;
  font-size: 12px;
  font-weight: 400;
`;

const DoughnutBoxSize = styled.div`
  width: 169px;
  height: 169px;
`;

const DoughnutBoxRightContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 40px;
`;

const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  /* justify-content: space-between; */
  align-items: center;
  gap: 40px;
`;

const LegendItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendSwatch = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ color }) => color};
`;

const LegendName = styled.div`
  color: #41415a;
  font-weight: 500;
  font-size: 14px;
`;

const LegendValue = styled.div`
  color: #1f2130;
  font-weight: 600;
  font-size: 14px;
`;

const StarHeader = styled.div`
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 8px;
  color: #656f7d;
`;

const StarBox = styled.div`
  display: flex;
  align-items: center;
  color: #656f7d;
  gap: 12px;
  font-size: 14px;
  font-weight: 600;
`;

const StarRectangle = styled.div``;

const StatsArea = styled.div`
  background-color: rgba(91, 145, 245, 0.05);
  color: ${({ theme }) => theme.palette.blueColor};
  padding: 8px;
  border-radius: 8px;
  display: grid;
  grid-template-columns: auto 14px;
  gap: 4px;
  align-items: center;

  &.red {
    color: #ff0500;
  }

  &.green {
    color: #22b231;
  }

  & > svg {
    width: 14px;
    height: 14px;
  }
`;

const LeftBox = styled.div`
  background-color: #fafafa;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
